import { type ReactNode, useMemo } from 'react';
import type { ReadinessState } from '@erp/shared-utils';
import { T, useGT } from 'gt-react';
import { CheckCircleIcon, TrashIcon } from '@phosphor-icons/react';
import {
  MultiSelect,
  type MultiSelectOption,
} from 'src/components/common/MultiSelect';
import { Input } from 'src/components/ui/input';
import { Toggle } from 'src/components/ui/toggle';
import { VendorFilter } from '../../../accounts-payable/payments/components/PaymentsListFilters';
import {
  ALL_LINE_TYPES,
  toBuyingLineTypes,
  type BuyingOrderLineType,
} from '../utils/buyingOrdersUrl';
import { filterSummary } from '../utils/filterSummary';
import {
  toReadinessStates,
  useReadinessOptions,
} from '../utils/readinessOptions';

interface BuyingFilterControlsProps {
  divisionOptions: MultiSelectOption[];
  selectedDivisionOptions: MultiSelectOption[];
  onDivisionChange: (ids: string[]) => void;
  warehouseOptions: MultiSelectOption[];
  selectedWarehouseOptions: MultiSelectOption[];
  warehouseTypeById: Map<string, string | null>;
  onWarehouseChange: (ids: string[]) => void;
  vendorIds: string[];
  onVendorChange: (ids: string[]) => void;
  /** Shown in place of the vendor filter: the transfer board's source filter. */
  ownerFilter?: ReactNode;
  /** The order readiness filter; off on the transfer board, which has none. */
  showReadiness?: boolean;
  buyerOptions: MultiSelectOption[];
  buyerCodes: string[];
  onBuyerChange: (codes: string[]) => void;
  readiness: ReadinessState[];
  onReadinessChange: (states: ReadinessState[]) => void;
  /** Days-on-order floor (GEN-3151); undefined lists every age. */
  minDaysOnOrder: number | undefined;
  onMinDaysOnOrderChange: (days: number | undefined) => void;
  lineTypes: BuyingOrderLineType[];
  onLineTypesChange: (types: BuyingOrderLineType[]) => void;
  showTrashed: boolean;
  onShowTrashedChange: (next: boolean) => void;
  /** Lines released since the last refresh are listed (GEN-3119). */
  showReleased: boolean;
  onShowReleasedChange: (next: boolean) => void;
}

/** Line-type picker options, labelled once for the picker and the summary. */
export function useLineTypeOptions(): MultiSelectOption[] {
  const gt = useGT();
  return useMemo(
    () =>
      ALL_LINE_TYPES.map((id) => ({
        id,
        label:
          id === 'back_order'
            ? gt('Back order', { context: 'buying line type filter option' })
            : id === 'potf'
              ? gt('POTF', {
                  context: 'buying line type filter option, product on the fly',
                })
              : gt('Stock', { context: 'buying line type filter option' }),
      })),
    [gt],
  );
}

interface CodeFilterProps {
  widthClass: string;
  options: MultiSelectOption[];
  selectedIds: readonly string[];
  onChange: (ids: string[]) => void;
  /** Placeholder and accessible name. */
  label: string;
  /** Trigger text when nothing is narrowed. */
  allLabel: string;
  /** Trigger text prefix once the picks no longer fit inline. */
  prefix: string;
  /** Apply on close instead of on every pick (lists that refetch). */
  deferUpdate?: boolean;
  /** Keep the options in their given order. */
  keepOrder?: boolean;
  /** Selecting every option still narrows (uncoded lines drop out). */
  everyPickNarrows?: boolean;
}

/** A multi-select that names its picks in the trigger, as every code filter does. */
export function CodeFilter({
  widthClass,
  options,
  selectedIds,
  onChange,
  label,
  allLabel,
  prefix,
  deferUpdate = false,
  keepOrder = false,
  everyPickNarrows = false,
}: CodeFilterProps) {
  const selected = new Set(selectedIds);
  return (
    <div className={widthClass}>
      <MultiSelect
        options={options}
        value={options.filter((o) => selected.has(o.id))}
        onChange={(opts) => onChange(opts.map((o) => o.id))}
        placeholder={label}
        aria-label={label}
        summaryText={(selected, total) =>
          filterSummary({
            selectedLabels: selected.map((o) => o.label),
            totalCount: total,
            allLabel,
            prefix,
            everyPickNarrows,
          })
        }
        size="sm"
        className="h-7 text-xs"
        fullWidth
        hideChipsWhenAllSelected
        liveUpdate={!deferUpdate}
        shouldAutoSort={!keepOrder}
      />
    </div>
  );
}

/** A location option: its name, with its warehouse type at the right. */
function WarehouseOptionLabel({
  label,
  type,
}: {
  label: string;
  type: string | null | undefined;
}) {
  return (
    <span className="flex w-full items-center justify-between gap-2">
      <span className="truncate">{label}</span>
      {type && (
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">
          {type}
        </span>
      )}
    </span>
  );
}

/**
 * Robert (2026-10-19): days on order belongs in the search up top, not in a
 * header column, so a buyer can pull up every location with items sitting
 * unordered past, say, seven days. Blank lists every age.
 */
function DaysOnOrderFilter({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (days: number | undefined) => void;
}) {
  const gt = useGT();
  return (
    <label className="flex h-7 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
      <T context="replenishment days-on-order filter label, followed by a number input">
        Days on order ≥
      </T>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        value={value ?? ''}
        onChange={(e) => {
          const next = e.target.valueAsNumber;
          onChange(Number.isInteger(next) && next >= 0 ? next : undefined);
        }}
        aria-label={gt('Days on order, at least', {
          context: 'replenishment days-on-order filter aria label',
        })}
        className="h-7 w-16 text-xs"
      />
    </label>
  );
}

/** The editable filter row shown when the Filters section is open. */
// fallow-ignore-next-line complexity -- prop wiring only; each picker's logic lives in CodeFilter
export function BuyingFilterControls({
  divisionOptions,
  selectedDivisionOptions,
  onDivisionChange,
  warehouseOptions,
  selectedWarehouseOptions,
  warehouseTypeById,
  onWarehouseChange,
  vendorIds,
  onVendorChange,
  ownerFilter,
  showReadiness = true,
  buyerOptions,
  buyerCodes,
  onBuyerChange,
  readiness,
  onReadinessChange,
  minDaysOnOrder,
  onMinDaysOnOrderChange,
  lineTypes,
  onLineTypesChange,
  showTrashed,
  onShowTrashedChange,
  showReleased,
  onShowReleasedChange,
}: BuyingFilterControlsProps) {
  const gt = useGT();
  const lineTypeOptions = useLineTypeOptions();
  const readinessOptions = useReadinessOptions();
  const divisionPlaceholder = gt('Select divisions...', {
    context: 'replenishment division filter placeholder',
  });

  return (
    <div className="flex flex-wrap items-center gap-2 p-2">
      <div className="w-[200px]">
        <MultiSelect
          options={divisionOptions}
          value={selectedDivisionOptions}
          onChange={(opts) => onDivisionChange(opts.map((o) => o.id))}
          placeholder={divisionPlaceholder}
          aria-label={gt('Division', { context: 'division filter' })}
          // Labelled, so a bare "20/20 selected" or code never reads as data.
          summaryText={(selected, total) =>
            selected.length === 0
              ? divisionPlaceholder
              : filterSummary({
                  selectedLabels: selected.map((o) => o.label),
                  totalCount: total,
                  allLabel: gt('All divisions', {
                    context:
                      'replenishment division filter summary, nothing narrowed',
                  }),
                  prefix: gt('Division', {
                    context: 'replenishment division filter summary prefix',
                  }),
                  namePicks: true,
                })
          }
          size="sm"
          className="h-7 text-xs"
          fullWidth
          hideChipsWhenAllSelected
          // Defer the URL/refetch until the buyer closes the popover.
          liveUpdate={false}
        />
      </div>

      <div className="w-[200px]">
        <MultiSelect
          options={warehouseOptions}
          value={selectedWarehouseOptions}
          onChange={(opts) => onWarehouseChange(opts.map((o) => o.id))}
          placeholder={gt('All warehouses', {
            context: 'replenishment warehouse filter placeholder',
          })}
          summaryText={(selected, total) =>
            filterSummary({
              selectedLabels: selected.map((o) => o.label),
              totalCount: total,
              allLabel: gt('All locations', {
                context:
                  'replenishment location filter summary, nothing narrowed',
              }),
              prefix: gt('Location', {
                context: 'replenishment location filter summary prefix',
              }),
            })
          }
          disabled={selectedDivisionOptions.length === 0}
          size="sm"
          className="h-7 text-xs"
          fullWidth
          hideChipsWhenAllSelected
          liveUpdate={false}
          renderOptionLabel={(o) => (
            <WarehouseOptionLabel
              label={o.label}
              type={warehouseTypeById.get(o.id)}
            />
          )}
        />
      </div>

      {ownerFilter ?? (
        <VendorFilter
          value={vendorIds}
          onChange={onVendorChange}
          className="h-7 min-w-[160px] text-xs"
        />
      )}

      <CodeFilter
        widthClass="w-[150px]"
        options={buyerOptions}
        selectedIds={buyerCodes}
        onChange={onBuyerChange}
        label={gt('Buyer', {
          context: 'replenishment buyer filter placeholder',
        })}
        allLabel={gt('All buyers', {
          context: 'replenishment buyer filter summary, nothing narrowed',
        })}
        prefix={gt('Buyers', {
          context: 'replenishment buyer filter summary prefix',
        })}
        deferUpdate
        everyPickNarrows
      />

      <CodeFilter
        widthClass="w-[170px]"
        options={lineTypeOptions}
        selectedIds={lineTypes}
        onChange={(ids) => onLineTypesChange(toBuyingLineTypes(ids))}
        label={gt('Line type', {
          context: 'replenishment line type filter aria label',
        })}
        allLabel={gt('All line types', {
          context: 'replenishment line type filter summary, nothing narrowed',
        })}
        prefix={gt('Line type', {
          context: 'replenishment line type filter summary prefix',
        })}
        keepOrder
      />

      {showReadiness && (
        <CodeFilter
          widthClass="w-[150px]"
          options={readinessOptions}
          selectedIds={readiness}
          onChange={(ids) => onReadinessChange(toReadinessStates(ids))}
          label={gt('Order readiness', {
            context: 'replenishment order readiness filter aria label',
          })}
          allLabel={gt('Any readiness', {
            context:
              'replenishment order readiness filter summary, nothing narrowed',
          })}
          prefix={gt('Readiness', {
            context: 'replenishment order readiness filter summary prefix',
          })}
          keepOrder
        />
      )}

      <DaysOnOrderFilter
        value={minDaysOnOrder}
        onChange={onMinDaysOnOrderChange}
      />

      <Toggle
        pressed={showTrashed}
        onPressedChange={onShowTrashedChange}
        variant="outline"
        size="sm"
        className="h-7 whitespace-nowrap text-xs data-[pressed]:bg-gray-200"
        aria-label={gt('Show rejected suggestions', {
          context: 'replenishment show-trashed filter aria label',
        })}
      >
        <TrashIcon className="mr-1 h-3.5 w-3.5" />
        <T context="replenishment show-rejected filter label">Rejected</T>
      </Toggle>

      <Toggle
        pressed={showReleased}
        onPressedChange={onShowReleasedChange}
        variant="outline"
        size="sm"
        className="h-7 whitespace-nowrap text-xs data-[pressed]:bg-gray-200"
        aria-label={gt('Show lines released since the last refresh', {
          context: 'replenishment show-released filter aria label',
        })}
      >
        <CheckCircleIcon className="mr-1 h-3.5 w-3.5" />
        {showReleased ? (
          <T context="replenishment show-released filter label, released lines listed">
            Showing released
          </T>
        ) : (
          <T context="replenishment show-released filter label, released lines hidden">
            Hiding released
          </T>
        )}
      </Toggle>
    </div>
  );
}
